import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import {
  BucketAsset,
  BucketNumberEnum,
  useListBucketsQuery,
} from "features/trading/buckets";
import { sortMembers } from "./constants";

export const ALL_BUCKETS = "all";

export type BucketScope = BucketNumberEnum | typeof ALL_BUCKETS;

const DEFAULT_BUCKET_NUMBERS = [
  BucketNumberEnum.BUCKET_1,
  BucketNumberEnum.BUCKET_2,
];

interface BucketAssetSelectorProps {
  accountId?: number | null;
  bucketNumbers?: BucketNumberEnum[];
  scope: BucketScope;
  onAssetIdsChange: (assetIds: number[]) => void;
  onScopeChange: (scope: BucketScope) => void;
}

/**
 * Pick a bucket or All - then optionally narrow to specific assets inside it.
 * The asset options are the buckets' own assetsInScope, already loaded alongside the
 * bucket, so this does not search the global asset catalogue
 */
export const BucketAssetSelector = ({
  accountId,
  bucketNumbers = DEFAULT_BUCKET_NUMBERS,
  scope,
  onAssetIdsChange,
  onScopeChange,
}: BucketAssetSelectorProps) => {
  const [selectedAssetIds, setSelectedAssetIds] = useState<number[]>([]);

  const { data: bucketsData } = useListBucketsQuery(
    { account: accountId as number },
    { skip: !accountId },
  );

  const assetsIn = useCallback(
    (numbers: BucketNumberEnum[]) => {
      const unique: BucketAsset[] = [];
      numbers.forEach((number) => {
        bucketsData?.results
          .find((bucket) => bucket.number === number)
          ?.assets.filter(({ included }) => included)
          .forEach((bucketAsset) => {
            // The same asset can sit in both buckets - take it once.
            if (!unique.some(({ asset }) => asset === bucketAsset.asset)) {
              unique.push(bucketAsset);
            }
          });
      });
      return sortMembers(unique);
    },
    [bucketsData],
  );

  // Bucket 3 is a scope of the account's own rows, on its own account — not
  // one of the buckets whose assets can be narrowed here.
  const assetBucketNumbers = useMemo<BucketNumberEnum[]>(
    () =>
      bucketNumbers.filter((number) => number !== BucketNumberEnum.BUCKET_3),
    [bucketNumbers],
  );

  const isBucket3 = scope === BucketNumberEnum.BUCKET_3;

  const allAssets = useMemo(
    () => assetsIn(assetBucketNumbers),
    [assetsIn, assetBucketNumbers],
  );

  const scopeAssets = useCallback(
    (target: BucketScope) => {
      let inScope: BucketAsset[] = [];
      if (target === ALL_BUCKETS) {
        inScope = allAssets;
      } else if (assetBucketNumbers.includes(target)) {
        inScope = assetsIn([target]);
      }
      const picked = inScope.filter(({ asset }) =>
        selectedAssetIds.includes(asset),
      );
      return {
        inScope,
        assetIds: (picked.length ? picked : inScope).map(({ asset }) => asset),
      };
    },
    [allAssets, assetBucketNumbers, assetsIn, selectedAssetIds],
  );

  const { inScope: assetsInScope, assetIds } = useMemo(
    () => scopeAssets(scope),
    [scopeAssets, scope],
  );

  const selectedAssets = useMemo(
    () => allAssets.filter(({ asset }) => selectedAssetIds.includes(asset)),
    [allAssets, selectedAssetIds],
  );

  useEffect(() => {
    onAssetIdsChange(assetIds);
  }, [onAssetIdsChange, assetIds]);

  const handleScopeChange = (_: unknown, value: BucketScope | null) => {
    if (value === null) return;
    onScopeChange(value);
    onAssetIdsChange(scopeAssets(value).assetIds);
  };

  const handleAssetsChange = (_: unknown, value: BucketAsset[]) => {
    setSelectedAssetIds(value.map(({ asset }) => asset));
  };

  return (
    <Box display="flex" gap={2} alignItems="flex-start" flexWrap="wrap">
      <ToggleButtonGroup
        exclusive
        size="small"
        color="primary"
        value={scope}
        onChange={handleScopeChange}
      >
        <ToggleButton
          value={ALL_BUCKETS}
          sx={{ paddingX: 2, gap: 1, textTransform: "none" }}
        >
          All
          <Chip
            size="small"
            label={allAssets.length}
            color={scope === ALL_BUCKETS ? "primary" : "default"}
          />
        </ToggleButton>
        {bucketNumbers.map((number) => (
          <ToggleButton
            key={number}
            value={number}
            sx={{ paddingX: 2, gap: 1, textTransform: "none" }}
          >
            Bucket {number}
            {assetBucketNumbers.includes(number) && (
              <Chip
                size="small"
                label={assetsIn([number]).length}
                color={number === scope ? "primary" : "default"}
              />
            )}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {isBucket3 ? null : (
        <Autocomplete<BucketAsset, true>
          multiple
          size="small"
          options={assetsInScope}
          value={selectedAssets}
          onChange={handleAssetsChange}
          getOptionLabel={({ symbol, asset__name }) =>
            `${symbol} - ${asset__name}`
          }
          isOptionEqualToValue={(option, value) => option.asset === value.asset}
          sx={{ flexGrow: 1, minWidth: 280 }}
          noOptionsText="No assets in scope"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Assets"
              placeholder={selectedAssets.length ? "" : "All assets in scope"}
            />
          )}
        />
      )}
    </Box>
  );
};

export default BucketAssetSelector;
